import React, { useState, useRef, useEffect, memo, useMemo, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Link,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
  Fab,
  Fade,
  Zoom,
  Dialog,
  ListItemButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  AppBar,
  Toolbar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  InputAdornment,
  Collapse,
  GlobalStyles,
  Drawer,
  CircularProgress,
} from '@mui/material';

import parse from 'html-react-parser';
import type { Layout } from 'plotly.js';
import Plot from 'react-plotly.js';
import { motion, useInView } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import ScienceIcon from '@mui/icons-material/Science';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import AdjustIcon from '@mui/icons-material/Adjust';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import FactoryIcon from '@mui/icons-material/Factory';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LoginIcon from '@mui/icons-material/Login';
import AreaChartIcon from '@mui/icons-material/AreaChart';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import TelegramIcon from '@mui/icons-material/Telegram';
import MenuIcon from '@mui/icons-material/Menu';
import HubIcon from '@mui/icons-material/Hub';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import CycloneIcon from '@mui/icons-material/Cyclone';
import WaveIcon from '@mui/icons-material/Waves';
import LightModeIcon from '@mui/icons-material/LightMode';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import BiotechIcon from '@mui/icons-material/Biotech';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import VideocamIcon from '@mui/icons-material/Videocam';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AirIcon from '@mui/icons-material/Air';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useLanguage } from './hooks/useLanguage';

// ----------------------------------------------------------------------------
// Хук для определения мобильного устройства
// ----------------------------------------------------------------------------
const useMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = typeof window !== 'undefined' && (
        window.innerWidth <= 768 ||
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent)
      );
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const ASSETS_PATH = 'visiting-card/';

// Типы для таблицы
interface ShotData {
  id: number;
  time: string;
  gas: string;
  pressure: string;
  bphi: number;
  ip: number;
  ne: string;
  plasma: boolean;
  comment: string;
}

// Данные для таблицы
const shotRows: ShotData[] = [
  { id: 3484, time: '2026-02-17', gas: 'H2', pressure: '9.85e-05', bphi: 107.91, ip: 7.40, ne: '1.66e+18', plasma: true, comment: 'normal TF 30' },
  { id: 3624, time: '2026-03-04', gas: 'H2', pressure: '1.29e-04', bphi: 105.38, ip: 5.22, ne: '2.14e+18', plasma: true, comment: 'normal TF300 Graphite li' },
  { id: 3647, time: '2026-03-05', gas: 'H2', pressure: '1.32e-04', bphi: 105.47, ip: 7.35, ne: '1.91e+18', plasma: true, comment: 'Normal TF300 Graphite li' },
  { id: 3673, time: '2026-03-06', gas: 'H2', pressure: '1.26e-04', bphi: 115.17, ip: 11.48, ne: '8.10e+17', plasma: true, comment: 'Normal TF320 Graphite li' },
  { id: 3721, time: '2026-03-11', gas: 'H2', pressure: '1.11e-04', bphi: 122.63, ip: 11.90, ne: '8.41e+17', plasma: true, comment: 'Normal TF340 Graphite li' },
  { id: 3736, time: '2026-03-11', gas: 'H2', pressure: '9.85e-05', bphi: 120.61, ip: 14.37, ne: '8.03e+17', plasma: true, comment: 'Normal TF340 Graphite li' },
  { id: 3788, time: '2026-03-19', gas: 'H2', pressure: '9.24e-06', bphi: 135.39, ip: 11.23, ne: '9.75e+17', plasma: true, comment: 'Normal TF340 Graphite lim pulse valve' },
  { id: 3807, time: '2026-03-19', gas: 'H2', pressure: '1.85e-05', bphi: 133.78, ip: 12.04, ne: '4.71e+17', plasma: true, comment: 'Normal TF340 Graphite lim pulse valve' },
  { id: 3934, time: '2026-04-03', gas: 'H2', pressure: '2.16e-05', bphi: 134.96, ip: 7.06, ne: '6.90e+17', plasma: true, comment: 'Normal TF340 Graphite lim pulse valve' },
  { id: 3938, time: '2026-04-03', gas: 'H2', pressure: '9.8e-05', bphi: 134.69, ip: 10.51, ne: '7.07e+17', plasma: true, comment: 'Normal TF340 Graphite lim pulse valve' },
  { id: 3951, time: '2026-04-06', gas: 'H2', pressure: '9.8e-05', bphi: 134.93, ip: 7.85, ne: '6.92e+17', plasma: true, comment: 'norm TF340 graphite fast valve' },
  { id: 3970, time: '2026-04-07', gas: 'H2', pressure: '9.8e-05', bphi: 135.95, ip: 7.78, ne: '6.24e+17', plasma: true, comment: 'norm TF340 graphite fast valve' }
];

const diagnosticIconsList = [
  <AutoModeIcon />,   // Пояса Роговского
  <AdjustIcon />,     // Loop voltage
  <CycloneIcon />,    // Diamagnetic loops
  <WorkspacesIcon />, // Magnetic probes
  <PodcastsIcon />,   // RF power
  <WaveIcon />,       // Microwave interferometers
  <LightModeIcon />,  // H-alpha
  <QueryStatsIcon />, // Spectrometers
  <GraphicEqIcon />,  // X-ray
  <BlurOnIcon />,     // Bolometers
  <VideocamIcon />,   // Fast camera
  <BiotechIcon />,    // Material probe
  <AlignVerticalBottomIcon />, // Langmuir probes
  <AirIcon />,        // Vacuum
];

interface DiagnosticTranslation {
  title: string;
  description: string[];
  chips: string[];
}

type Order = 'asc' | 'desc';

// ============================================================================
// КОМПОНЕНТ КАРТОЧКИ С ИЗОБРАЖЕНИЕМ И HOVER-ЭФФЕКТОМ
// ============================================================================
const ImageCard: React.FC<{
  src: string;
  alt: string;
  height?: number | string;
  onClick: () => void;
  sx?: any;
  t: (key: string) => string;
}> = ({ src, alt, height = 200, onClick, sx, t }) => {
  return (
    <Card
      sx={{
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover .image-overlay': {
          opacity: 1,
        },
        '&:hover .image-media': {
          transform: 'scale(1.05)',
        },
        ...sx,
      }}
      onClick={onClick}
    >
      <CardMedia
        component="img"
        height={height}
        image={src}
        alt={alt}
        className="image-media"
        sx={{
          objectFit: 'cover',
          transition: 'transform 0.3s ease-in-out',
        }}
        onError={(e: any) => (e.currentTarget.src = 'https://via.placeholder.com/400x200?text=No+Image')}
      />
      {/* Overlay с иконкой и текстом */}
      <Box
        className="image-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: alpha('#000', 0.6),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        <ZoomInIcon sx={{ fontSize: 40, color: 'white', mb: 1 }} />
        <Typography variant="body1" color="white" fontWeight={600}>
          {t('image_zoom_text')}
        </Typography>
      </Box>
    </Card>
  );
};

// ============================================================================
// МЕМОИЗИРОВАННОЕ МОДАЛЬНОЕ ОКНО УВЕЛИЧЕНИЯ ИЗОБРАЖЕНИЯ
// ============================================================================
const ImageDialog = memo(({ open, onClose, src, alt }: { open: boolean; onClose: () => void; src: string; alt: string }) => {
  const imgRef = useRef<HTMLImageElement>(null);

  const handleFullscreen = () => {
    if (imgRef.current) {
      imgRef.current.requestFullscreen().catch(err => {
        console.error('Failed to enter fullscreen:', err);
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {alt}
        <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
          <IconButton onClick={handleFullscreen} aria-label="Увеличить">
            <FullscreenIcon />
          </IconButton>
          <IconButton onClick={onClose} aria-label="Закрыть">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'white' }}>
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          onError={(e: any) => {
            e.currentTarget.src = 'https://via.placeholder.com/800x600?text=No+Image+for+this+Shot';
          }}
        />
      </DialogContent>
    </Dialog>
  );
});
// ============================================================================
// МЕМОИЗИРОВАННЫЙ КОМПОНЕНТ СЕКЦИИ (с поддержкой мобильных)
// ============================================================================
const Section = memo(({ children, index }: { children: React.ReactNode; index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const isMobile = useMobile();

  useEffect(() => {
    if (!isMobile && isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated, isMobile]);

  if (isMobile) {
    return <div ref={ref}>{children}</div>;
  }

  const isEven = index % 2 === 0;
  const variants = {
    hidden: {
      opacity: 0,
      x: isEven ? -300 : 300,
      transition: { duration: 1 },
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <motion.div ref={ref} initial="hidden" animate={hasAnimated ? 'visible' : 'hidden'} variants={variants}>
      {children}
    </motion.div>
  );
});

// ============================================================================
// КОМПОНЕНТ СЛАЙДШОУ ДЛЯ СЕКЦИИ "ТОКАМАК ПОД КЛЮЧ"
// ============================================================================
const TurnkeySlideshow: React.FC<{
  t: (key: string) => string;
  ASSETS_PATH: string;
  openImageDialog: (src: string, alt: string) => void;
}> = ({ t, ASSETS_PATH, openImageDialog }) => {
  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      src: `${ASSETS_PATH}turnkey.jpg`,
      alt: t('turnkey_slideshow.turnkey_title'),
      title: t('turnkey_slideshow.turnkey_title'),
      desc: '',
    },
    {
      src: `${ASSETS_PATH}mephist0.jpg`,
      alt: t('turnkey_slideshow.mephist0_title'),
      title: t('turnkey_slideshow.mephist0_title'),
      desc: '',
    },
    {
      src: `${ASSETS_PATH}server.jpg`,
      alt: t('turnkey_slideshow.server_title'),
      title: t('turnkey_slideshow.server_title'),
      desc: t('turnkey_slideshow.server_desc'),
    },
    {
      src: `${ASSETS_PATH}charger.jpg`,
      alt: t('turnkey_slideshow.charger_title'),
      title: t('turnkey_slideshow.charger_title'),
      desc: t('turnkey_slideshow.charger_desc'),
    },
    {
      src: `${ASSETS_PATH}capacitors.jpg`,
      alt: t('turnkey_slideshow.capacitors_title'),
      title: t('turnkey_slideshow.capacitors_title'),
      desc: t('turnkey_slideshow.capacitors_desc'),
    },
    {
      src: `${ASSETS_PATH}vacuum.jpg`,
      alt: t('turnkey_slideshow.vacuum_title'),
      title: t('turnkey_slideshow.vacuum_title'),
      desc: t('turnkey_slideshow.vacuum_desc'),
    },
    {
      src: `${ASSETS_PATH}daq.jpg`,
      alt: t('turnkey_slideshow.daq_title'),
      title: t('turnkey_slideshow.daq_title'),
      desc: t('turnkey_slideshow.daq_desc'),
    },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 60000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          '&:hover .slideshow-overlay': { opacity: 1 },
          '&:hover .slideshow-media': { transform: 'scale(1.05)' },
          borderRadius: 4,
        }}
      >
        <CardMedia
          component="img"
          height={600}
          image={slides[currentSlide].src}
          alt={slides[currentSlide].alt}
          className="slideshow-media"
          sx={{
            objectFit: 'cover',
            transition: 'transform 0.5s ease-in-out',
            borderRadius: 4,
          }}
          onClick={() => openImageDialog(slides[currentSlide].src, slides[currentSlide].alt)}
          onError={(e: any) => (e.currentTarget.src = 'https://via.placeholder.com/600x400?text=No+Image')}
        />

        {/* Overlay с иконкой увеличения */}
        <Box
          className="slideshow-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: alpha('#000', 0.4),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
            cursor: 'pointer',
          }}
          onClick={() => openImageDialog(slides[currentSlide].src, slides[currentSlide].alt)}
        >
          <ZoomInIcon sx={{ fontSize: 50, color: 'white' }} />
        </Box>

        {/* Подпись к слайду */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: alpha('#000', 0.7),
            color: 'white',
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {slides[currentSlide].title}
          </Typography>
          {slides[currentSlide].desc && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {slides[currentSlide].desc}
            </Typography>
          )}
        </Box>
      </Card>

      {/* Кнопки навигации */}
      <IconButton
        onClick={prevSlide}
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 4, sm: 8 },
          transform: 'translateY(-50%)',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
          },
          width: { xs: 36, sm: 48 },
          height: { xs: 36, sm: 48 },
          zIndex: 2,
        }}
      >
        <KeyboardArrowLeftIcon fontSize={useMobile() ? 'small' : 'medium'} />
      </IconButton>

      <IconButton
        onClick={nextSlide}
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 4, sm: 8 },
          transform: 'translateY(-50%)',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
          },
          width: { xs: 36, sm: 48 },
          height: { xs: 36, sm: 48 },
          zIndex: 2,
        }}
      >
        <KeyboardArrowRightIcon fontSize={useMobile() ? 'small' : 'medium'} />
      </IconButton>

      {/* Индикаторы слайдов (точки) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 2,
          mb: 1,
        }}
      >
        {slides.map((_, index) => (
          <Box
            key={index}
            onClick={() => goToSlide(index)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: currentSlide === index ? 'primary.main' : alpha(theme.palette.primary.main, 0.3),
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.99),
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};





// ============================================================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================================================
const VisitingCard: React.FC = () => {
  const { t, toggleLanguage } = useLanguage();
  const theme = useTheme();
  const [currentShotId, setCurrentShotId] = useState<number | null>(3970);
  const keyFeaturesTexts = t('key_feature_items') as unknown as string[];
  const keyFeatureIcons = [
    <StarIcon color="primary" key={0} />,
    <HubIcon color="primary" key={1} />,
    <FactoryIcon color="primary" key={2} />,
    <ScienceIcon color="primary" key={3} />,
    <GroupsIcon color="primary" key={4} />,
    <AreaChartIcon color="primary" key={5} />,
    <SchoolIcon color="primary" key={6} />,
    <SchoolIcon color="primary" key={7} />,
  ];

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [leftRatio, setLeftRatio] = useState(30); // начальное соотношение 30%

  const heroRef = useRef<HTMLDivElement>(null);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  // === Новое: состояние для источника видео и обработка ошибки загрузки ===
  const [videoSrc, setVideoSrc] = useState<string>(`${ASSETS_PATH}video/plasma.webm`);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    setVideoError(false);
    if (currentShotId) {
      setVideoSrc(`${ASSETS_PATH}video/${currentShotId}.webm`);
    } else {
      setVideoSrc(`${ASSETS_PATH}video/plasma.webm`);
    }
  }, [currentShotId]);

  const handleVideoError = useCallback(() => {
    if (!videoError) {
      setVideoError(true);
      setVideoSrc(`${ASSETS_PATH}video/plasma.webm`);
    }
  }, [videoError]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const totalWidth = rect.width;
      let newRatio = (x / totalWidth) * 100;
      newRatio = Math.min(Math.max(newRatio, 20), 80);
      setLeftRatio(newRatio);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const graphCardRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    description: useRef<HTMLDivElement>(null!),
    parameters: useRef<HTMLDivElement>(null!),
    diagnostics: useRef<HTMLDivElement>(null!),
    online: useRef<HTMLDivElement>(null!),
    turnkey: useRef<HTMLDivElement>(null!),
    publications: useRef<HTMLDivElement>(null!),
    contacts: useRef<HTMLDivElement>(null!),
  };

  // Устанавливаем заголовок страницы
  useEffect(() => {
    document.title = t('title');
  }, [t]);

  const [expandedDiagnostic, setExpandedDiagnostic] = useState<number | null>(null);
  const [expandedAnyDiagnostic, setExpandedAnyDiagnostic] = useState(false);
  const [closingDiagnostic, setClosingDiagnostic] = useState<number | null>(null);
  const [closingAny, setClosingAny] = useState(false);

  const diagnostics = t('diagnostics') as unknown as DiagnosticTranslation[];

  const [highlightCard, setHighlightCard] = useState(false);
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [shotData, setShotData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Загрузка данных выстрела
  useEffect(() => {
    if (currentShotId) {
      setLoading(true);
      fetch(`${ASSETS_PATH}shots/${currentShotId}.json`)
        .then((res) => res.json())
        .then((data) => {
          setShotData(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load shot data', err);
          setLoading(false);
        });
    } else {
      setShotData(null);
    }
  }, [currentShotId]);

  // Наблюдатели для активной секции (только один)
  const [activeSection, setActiveSection] = useState('');
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const options = { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' };
    Object.entries(sectionRefs).forEach(([key, ref]) => {
      if (ref.current) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(key);
            }
          });
        }, options);
        observer.observe(ref.current);
        observers.push(observer);
      }
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  useEffect(() => {
    if (!heroRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrolledPastHero(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);


  // ============================================================================
  // КОМПОНЕНТ ГРАФИКОВ
  // ============================================================================
  const ShotPlots: React.FC<{ data: any }> = ({ data }) => {
    const time = data.time;
    const signals = data.signals;

    const baseLayout = {
      margin: { l: 55, r: 35, b: 5, t: 10, pad: 0 },
      hovermode: 'x unified' as const,
      autosize: true,
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      font: { family: '"Montserrat", sans-serif', size: 10 },
      showlegend: true,
      legend: {
        x: 0.02,
        y: 0.98,
        xanchor: 'left' as const,
        yanchor: 'top' as const,
        bgcolor: 'rgba(255,255,255,0.7)',
        bordercolor: '#ccc',
        borderwidth: 1,
        font: { size: 10 },
      },
    };

    const makeTrace = (
      x: number[],
      y: number[],
      nameKey: string,
      color: string,
      dash: 'solid' | 'dash' = 'solid',
      yaxis: 'y' | 'y2' = 'y'
    ) => ({
      x,
      y,
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: t(nameKey),
      line: { color, dash, width: dash === 'dash' ? 2.5 : 3 },
      yaxis,
    });

    const createLayout = (
      yTitleKey: string,
      yRange?: [number | null, number | null],
      y2TitleKey?: string,
      y2Range?: [number | null, number | null],
      showXLabels: boolean = true,
      yTick?: number,
      y2Tick?: number
    ): Partial<Layout> => ({
      ...baseLayout,
      xaxis: {
        range: [5, 16],
        showticklabels: showXLabels,
        title: showXLabels ? { text: t('graph_xaxis_time'), font: { size: 12 } } : undefined,
        automargin: true,
        ticks: showXLabels ? 'inside' : '',
        ticklabelposition: 'inside',
        ticklen: showXLabels ? 4 : 0,
        tickwidth: showXLabels ? 1 : 0,
        tickcolor: '#333',
        showline: showXLabels,
        linecolor: '#333',
        linewidth: 0.5,
        zeroline: false,
        tickfont: { size: 10 },
        dtick: 1,
        showgrid: true,
        tickformat: '.1f',
        gridcolor: '#f0f0f0',
        gridwidth: 1,
      },
      yaxis: {
        title: { text: t(yTitleKey), font: { size: 12 }, standoff: 5 },
        range: yRange || [null, null],
        dtick: yTick,
        tickfont: { size: 10 },
        showgrid: true,
        gridcolor: '#f0f0f0',
        zeroline: true,
        zerolinecolor: '#ccc',
        zerolinewidth: 0.5,
        showline: true,
        linecolor: '#333',
        linewidth: 0.5,
        ticks: 'inside',
        ticklen: 4,
        tickwidth: 1,
        tickcolor: '#333',
      },
      ...(y2TitleKey
        ? {
            yaxis2: {
              title: { text: t(y2TitleKey), font: { size: 12 }, standoff: 5 },
              range: y2Range || [null, null],
              dtick: y2Tick,
              tickfont: { size: 10 },
              showgrid: false,
              zeroline: true,
              zerolinecolor: '#ccc',
              zerolinewidth: 0.5,
              overlaying: 'y',
              side: 'right',
              showline: true,
              ticks: 'inside',
              ticklen: -4,
              tickwidth: 1,
              tickcolor: '#333',
            },
          }
        : {}),
    });

    const heights = [170, 170, 170, 170, 140];

    return (
      <Box sx={{ height: '100%', bgcolor: 'white', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* График 1: Ip + VL */}
        {signals.Ip && signals.VL2 && signals.VL7 && (
          <Box sx={{ height: heights[0] }}>
            <Plot
              data={[
                makeTrace(time, signals.Ip, 'graph_legend_ip', '#0c4d8f', 'solid', 'y'),
                makeTrace(
                  time,
                  signals.VL2.map((v: number, i: number) => (v + signals.VL7[i]) / 2),
                  'graph_legend_vl_avg',
                  'red',
                  'solid',
                  'y2'
                ),
              ]}
              layout={createLayout('graph_yaxis_ip', [0, 12], 'graph_yaxis_vl', [0, 9], false, 2)}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true, displayModeBar: 'hover', displaylogo: false }}
            />
          </Box>
        )}

        {/* График 2: H-alpha + CIII + AXUV */}
        {signals.Halpha && (
          <Box sx={{ height: heights[1] }}>
            <Plot
              data={[
                makeTrace(time, signals.Halpha, 'graph_legend_halpha', 'magenta'),
                ...(signals.CIII ? [makeTrace(time, signals.CIII, 'graph_legend_ciii', '#0c4d8f')] : []),
                ...(signals.AXUV ? [makeTrace(time, signals.AXUV, 'graph_legend_axuv', 'green')] : []),
              ]}
              layout={createLayout('graph_yaxis_norm_intensity', [0, 1], undefined, undefined, false, 0.2)}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true, displayModeBar: 'hover', displaylogo: false }}
            />
          </Box>
        )}

        {/* График 3: Плотность */}
        {signals.density && (
          <Box sx={{ height: heights[2] }}>
            <Plot
              data={[makeTrace(time, signals.density, 'graph_legend_density', '#0c4d8f')]}
              layout={createLayout('graph_yaxis_density_product', undefined, undefined, undefined, false)}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true, displayModeBar: 'hover', displaylogo: false }}
            />
          </Box>
        )}

        {/* График 4: Токи катушек */}
        {signals.I_CS && signals.PF1 && signals.PF3 && (
          <Box sx={{ height: heights[3] }}>
            <Plot
              data={[
                makeTrace(time, signals.I_CS, 'graph_legend_cs', '#0c4d8f', 'solid', 'y'),
                makeTrace(time, signals.PF1, 'graph_legend_pf12', 'red', 'solid', 'y2'),
                makeTrace(time, signals.PF3.map((v: number) => v * 3), 'graph_legend_pf34_x3', 'green', 'solid', 'y2'),
              ]}
              layout={createLayout('graph_yaxis_ics', [0, 12], 'graph_yaxis_ipf', undefined, false, 2)}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true, displayModeBar: 'hover', displaylogo: false }}
            />
          </Box>
        )}

        {/* График 5: Тороидальное поле (с метками на оси X) */}
        {signals.Bphi && (
          <Box sx={{ height: heights[4] }}>
            <Plot
              data={[makeTrace(time, signals.Bphi, 'graph_legend_bphi', '#0c4d8f')]}
              layout={createLayout('graph_yaxis_bphi', [80, 140], undefined, undefined, true)}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true, displayModeBar: 'hover', displaylogo: false }}
            />
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const publicationsData = [
    {
      text: 'N. Efimov et al. Power Supply System for the MEPhIST-0 Tokamak Electromagnetic Coils // Physics of Atomic Nuclei. — 2025. — Vol. 88. — pp. 1722-1735. — ',
      doi: '10.1134/S1063778825090133',
    },
    {
      text: 'E. Vinitskiy et. al. Optimization of the toroidal magnetic coil system for the small spherical tokamak MEPhIST-0 // Fusion Science and Technology. — 2025. — Vol. 81. — No. 5.— pp. 485-494. — ',
      doi: '10.1080/15361055.2024.2431782',
    },
    {
      text: 'E. Vinitskiy, N. Efimov, A. Prishvitsyn, I. Sorokin, S. Krat Vertical stray field measurements using an electron beam in the MEPhIST-0 tokamak // Plasma Physics and Control Fusion. — 2024. — Vol. 66. — No. 1 — p. 015004. — ',
      doi: '10.1088/1361-6587/ad0d87',
    },
    {
      text: 'D. Ulasevich et al. Optimization of the Poloidal Magnetic System of the MEPHIST-0 Tokamak // Physics of Atomic Nuclei — 2024. — Vol. 86 — No. 7 — pp. 1555–1563. — ',
      doi: '10.1134/S1063778823070244',
    },
    {
      text: 'S. Krat et al. MEPhIST-0 Tokamak for Education and Research // Fusion Science and Technology. — 2023. — Vol. 79. —  No. 4. — pp. 446–464. — ',
      doi: '10.1080/15361055.2022.2149033',
    },
    {
      text: 'N. Efimov, F. Podolyako, A. Prishvitsyn, and S. Krat MEPhIST-0 integrated control and data acquisition system // Fusion Engineering and Design. — 2023. — Vol. 196. — p. 113990. — ',
      doi: '10.1016/j.fusengdes.2023.113990',
    },
    {
      text: 'N. Efimov et al. Measuring the Spectrum of the Soft Component of X-Ray Plasma Radiation at the MIFIST-0 Tokamak // Instruments and Experimental Techniques. — 2023. — Vol. 66. — No. 2. — pp. 257–262. — ',
      doi: '10.1134/S0020441223020148',
    },
    {
      text: 'A. Alieva et al. Microwave Preionization System of the MEPhIST-0 Tokamak // Physics of Atomic Nuclei. — 2022. — Vol. 85. — No. 12. — pp. 2082–2087. — ',
      doi: '10.1134/S1063778822090022',
    },
    {
      text: 'S. Krat et al. MEPHIST-0 Tokamak Toroidal Magnetic Field System // Physics of Atomic Nuclei. — 2021. — Vol. 84. — No. 12 — pp. 1995–2003. — ',
      doi: '10.1134/S1063778821120024',
    },
  ];

  const [heroImage, setHeroImage] = useState('tokamak.jpg');
  useEffect(() => {
    const images = ['tokamak.jpg', 'capacitor.jpg', 'capacitors.jpg', 'turnkey.jpg', 'vacuum.jpg'];
    const randomIndex = Math.floor(Math.random() * images.length);
    setHeroImage(images[randomIndex]);
  }, []);

  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [dialogImageSrc, setDialogImageSrc] = useState('');
  const [dialogImageAlt, setDialogImageAlt] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof ShotData>('id');

  const sortedRows = useMemo(() => {
    return [...shotRows].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (orderBy === 'plasma') {
        return order === 'asc' ? (a.plasma === b.plasma ? 0 : a.plasma ? -1 : 1) : a.plasma === b.plasma ? 0 : a.plasma ? 1 : -1;
      }
      return 0;
    });
  }, [order, orderBy]);

  const handleRequestSort = useCallback(
    (property: keyof ShotData) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const scrollToSection = useCallback((ref: React.RefObject<HTMLDivElement>, sectionId: string) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  }, []);

  const openImageDialog = useCallback((src: string, alt: string) => {
    setDialogImageSrc(src);
    setDialogImageAlt(alt);
    setImageDialogOpen(true);
  }, []);

  const experienceItems = t('experience_items') as unknown as string[];
  const controlFeatures = t('control_features') as unknown as string[];
  const optionalItems = t('optional_items') as unknown as Array<{ name: string; desc: string }>;
  const educationFeatures = t('education_features') as unknown as string[];
  const onlineServices = t('online_services') as unknown as Array<{ name: string; desc: string }>;
  const workshopItems = t('workshop_items') as unknown as string[];

  // ============================================================================
  // БЛОК ЗАКАЗА ИМПУЛЬСОВ
  // ============================================================================
  const ShotOrderCard = memo(({ onOrderClick }: { onOrderClick: () => void }) => {
    const [expanded, setExpanded] = useState(false);
    const [files, setFiles] = useState<File[]>([]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }, []);

    const removeFile = useCallback((index: number) => setFiles((prev) => prev.filter((_, i) => i !== index)), []);

    return (
      <Card sx={{ mt: 4, width: '100%' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', p: 2 }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="h6">{t('order_block_title')}</Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, p: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('order_block_desc')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth label={t('gas_type_label')} size="small">
                  <MenuItem value="">{t('select_gas_type')}</MenuItem>
                  <MenuItem value="argon">Argon</MenuItem>
                  <MenuItem value="helium">Helium</MenuItem>
                  <MenuItem value="hydrogen">Hydrogen</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('pressure_label')}
                  type="number"
                  size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('unit_mpa')}</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('magnetic_field_label')}
                  type="number"
                  size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('unit_mt')}</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inductor_label')}
                  type="number"
                  size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('unit_v')}</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label={t('notes_label')} multiline rows={2} size="small" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label={t('user_name_label')} size="small" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                  }}
                  onClick={() => document.getElementById('shot-file-input')?.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 30, color: 'text.secondary' }} />
                  <Typography variant="body2">{t('click_to_select_files')}</Typography>
                  <input id="shot-file-input" type="file" multiple hidden onChange={handleFileSelect} />
                </Box>
                {files.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {files.length} {t('files_attached')}
                    </Typography>
                    {files.map((f, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon fontSize="small" />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {f.name}
                        </Typography>
                        <IconButton size="small" onClick={() => removeFile(i)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button variant="contained" fullWidth onClick={onOrderClick} startIcon={<AddIcon />}>
                  {t('add_order')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Card>
    );
  });

  let sectionCounter = 0;
  const isMobile = useMobile();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <GlobalStyles
        styles={{
          html: {
            overflowX: 'hidden',
            overflowY: 'scroll',
          },
          body: {
            margin: 0,
            padding: 0,
            width: '100%',
            overflowX: 'hidden',
          },
          '*': {
            boxSizing: 'border-box',
          },
        }}
      />

      {/* Fixed Header */}
      <AppBar
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          px: { xs: 1, md: 2 },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            px: { xs: 1, md: 2 },
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            <IconButton
              sx={{ display: { xs: 'flex', lg: 'none' } }}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Меню"
            >
              <MenuIcon />
            </IconButton>

            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                gap: 1,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                minWidth: 0,
                flexShrink: 1,
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.primary.light,
                  borderRadius: 4,
                },
              }}
            >
              {[
                { id: 'description', label: t('nav_description') },
                { id: 'parameters', label: t('nav_parameters') },
                { id: 'diagnostics', label: t('nav_diagnostics') },
                { id: 'online', label: t('nav_online') },
                { id: 'turnkey', label: t('nav_turnkey') },
                { id: 'publications', label: t('nav_publications') },
                { id: 'contacts', label: t('nav_contacts') },
              ].map((item) => (
                <Button
                  key={item.id}
                  color="primary"
                  onClick={() => scrollToSection(sectionRefs[item.id as keyof typeof sectionRefs], item.id)}
                  sx={{
                    fontWeight: activeSection === item.id ? 700 : 400,
                    whiteSpace: 'nowrap',
                    px: 1,
                    minWidth: 'auto',
                    flexShrink: 0,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* ПРАВАЯ ЧАСТЬ: кнопки для десктопа + логотип */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              ml: 'auto',
            }}
          >
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1 }}>
              {!scrolledPastHero ? (
                <>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      whiteSpace: 'nowrap',
                      ml: 6,
                    }}
                    color="primary"
                    href="https://tokamak.mephi.ru/orders"
                    target="_blank"
                    rel="noopener"
                    startIcon={<LoginIcon />}
                  >
                    {t('enter_platform')}
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      whiteSpace: 'nowrap',
                    }}
                    onClick={toggleLanguage}
                    startIcon={<LanguageIcon />}
                  >
                    {t('toggle_language')}
                  </Button>
                </>
              ) : (
                <Tooltip title={t('modal_access_title')} arrow>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      whiteSpace: 'nowrap',
                      ml: 6,
                    }}
                    color="primary"
                    onClick={() => setAccessModalOpen(true)}
                    startIcon={<EmailIcon />}
                  >
                    {t('hero_cta')}
                  </Button>
                </Tooltip>
              )}
            </Box>

            {isMobile && scrolledPastHero && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setAccessModalOpen(true)}
                startIcon={<EmailIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                  mr: 4,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                }}
              >
                {t('hero_cta')}
              </Button>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: { xs: 1, sm: 2, md: 5 },
                mr: { xs: 1, sm: 2, md: 1 },
                my: 1,
                flexShrink: 1,
                minWidth: 0,
                '& img': {
                  height: { xs: 40, sm: 60, md: 90 },
                  width: 'auto',
                  maxWidth: '100%',
                  display: 'block',
                },
              }}
            >
              <img src={`${ASSETS_PATH}logo.png`} alt="MEPhIST-0" />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Toolbar />

      <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ width: 280, p: 2 }} role="presentation">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('drawer_menu_title')}</Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              href="https://tokamak.mephi.ru/orders"
              target="_blank"
              rel="noopener"
              fullWidth
              startIcon={<LoginIcon />}
              sx={{ justifyContent: 'flex-start', borderRadius: 3 }}
            >
              {t('enter_platform')}
            </Button>
            <Button
              variant="outlined"
              onClick={toggleLanguage}
              fullWidth
              startIcon={<LanguageIcon />}
              sx={{ justifyContent: 'flex-start', borderRadius: 3 }}
            >
              {t('toggle_language')}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            {[
              { id: 'description', label: t('nav_description') },
              { id: 'parameters', label: t('nav_parameters') },
              { id: 'diagnostics', label: t('nav_diagnostics') },
              { id: 'online', label: t('nav_online') },
              { id: 'turnkey', label: t('nav_turnkey') },
              { id: 'publications', label: t('nav_publications') },
              { id: 'contacts', label: t('nav_contacts') },
            ].map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => {
                  scrollToSection(sectionRefs[item.id as keyof typeof sectionRefs], item.id);
                  setMobileMenuOpen(false);
                }}
                sx={{
                  bgcolor: activeSection === item.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  borderRadius: 1,
                  mb: 0.5,
                  pl: 2,
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* HERO БЛОК */}
      <Box
        ref={heroRef}
        sx={{
          position: 'relative',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${ASSETS_PATH}${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.8,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.default} 100%)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12 }}>
              {isMobile ? (
                <div>
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    color="primary.dark"
                    gutterBottom
                    textAlign="center"
                    sx={{
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      fontSize: { xs: '2.2rem', sm: '3rem' },
                    }}
                  >
                    {t('hero_title')}
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    paragraph
                    textAlign="center"
                    sx={{
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                      fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    }}
                  >
                    {t('hero_subtitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setAccessModalOpen(true)}
                      sx={{ borderRadius: 4, px: 6, py: 1.5 }}
                    >
                      {t('hero_cta')}
                    </Button>
                  </Box>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    color="primary.dark"
                    gutterBottom
                    textAlign="center"
                    sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
                  >
                    {t('hero_title')}
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    paragraph
                    textAlign="center"
                    sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {t('hero_subtitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Tooltip title={t('modal_access_title')} arrow>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => setAccessModalOpen(true)}
                        sx={{ borderRadius: 4, px: 6, py: 1.5 }}
                      >
                        {t('hero_cta')}
                      </Button>
                    </Tooltip>
                  </Box>
                </motion.div>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* 1. Описание токамака */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.description} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography
              variant="h3"
              color="primary.dark"
              gutterBottom
              textAlign="center"
              sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
            >
              {t('section_description')}
            </Typography>
            <Typography variant="body1" paragraph textAlign="justify" sx={{ maxWidth: 'lg', mx: 'auto' }}>
              {t('tokamak_description')}
            </Typography>

            <Typography variant="h5" color="primary.dark" gutterBottom sx={{ mt: 4 }} textAlign="left">
              {t('key_features_title')}
            </Typography>
            <List sx={{ maxWidth: 'lg', mx: 'auto' }}>
              {keyFeaturesTexts.map((text, idx) => (
                <ListItem key={idx} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>{keyFeatureIcons[idx]}</ListItemIcon>
                  <ListItemText primary={parse(text)} sx={{ textAlign: 'justify' }} />
                </ListItem>
              ))}
            </List>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <ImageCard
                  src={`${ASSETS_PATH}tokamak2.jpg`}
                  alt={t('img_tokamak2')}
                  onClick={() => openImageDialog(`${ASSETS_PATH}tokamak2.jpg`, t('img_tokamak2'))}
                  t={t}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ImageCard
                  src={`${ASSETS_PATH}control-room.jpg`}
                  alt={t('img_control_room')}
                  onClick={() => openImageDialog(`${ASSETS_PATH}control-room.jpg`, t('img_control_room'))}
                  t={t}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ImageCard
                  src={`${ASSETS_PATH}capacitor.jpg`}
                  alt={t('img_capacitor')}
                  onClick={() => openImageDialog(`${ASSETS_PATH}capacitor.jpg`, t('img_capacitor'))}
                  t={t}
                />
              </Grid>
            </Grid>
          </div>
        </Section>

        {/* 2. Параметры разряда */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.parameters} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography
              variant="h3"
              color="primary.dark"
              gutterBottom
              sx={{ mt: 6, fontSize: { xs: '2rem', md: '3rem' } }}
              textAlign="center"
            >
              {t('section_parameters')}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TableContainer component={Paper} sx={{ mt: 1, borderRadius: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={orderBy === 'id' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'id'}
                            direction={orderBy === 'id' ? order : 'asc'}
                            onClick={() => handleRequestSort('id')}
                          >
                            {t('table_id')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'time' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'time'}
                            direction={orderBy === 'time' ? order : 'asc'}
                            onClick={() => handleRequestSort('time')}
                          >
                            {t('table_time')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'gas' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'gas'}
                            direction={orderBy === 'gas' ? order : 'asc'}
                            onClick={() => handleRequestSort('gas')}
                          >
                            {t('table_gas')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'pressure' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'pressure'}
                            direction={orderBy === 'pressure' ? order : 'asc'}
                            onClick={() => handleRequestSort('pressure')}
                          >
                            {t('table_pressure')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'bphi' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'bphi'}
                            direction={orderBy === 'bphi' ? order : 'asc'}
                            onClick={() => handleRequestSort('bphi')}
                          >
                            {t('table_bphi')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'ip' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'ip'}
                            direction={orderBy === 'ip' ? order : 'asc'}
                            onClick={() => handleRequestSort('ip')}
                          >
                            {t('table_ip')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'ne' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'ne'}
                            direction={orderBy === 'ne' ? order : 'asc'}
                            onClick={() => handleRequestSort('ne')}
                          >
                            {t('table_ne')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'plasma' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'plasma'}
                            direction={orderBy === 'plasma' ? order : 'asc'}
                            onClick={() => handleRequestSort('plasma')}
                          >
                            {t('table_plasma')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>{t('table_comment')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRows.map((row) => (
                        <Tooltip key={row.id} title={t('tooltip_see_shot')} arrow>
                          <TableRow
                            hover
                            onClick={() => {
                              setCurrentShotId(row.id);
                              if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
                              setHighlightCard(true);
                              highlightTimerRef.current = setTimeout(() => {
                                setHighlightCard(false);
                                highlightTimerRef.current = null;
                              }, 2000);
                              if (graphCardRef.current) {
                                graphCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.time}</TableCell>
                            <TableCell>{row.gas}</TableCell>
                            <TableCell>{row.pressure}</TableCell>
                            <TableCell>{row.bphi.toFixed(2)}</TableCell>
                            <TableCell>{row.ip.toFixed(2)}</TableCell>
                            <TableCell>{row.ne}</TableCell>
                            <TableCell>
                              {row.plasma ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                            </TableCell>
                            <TableCell>{row.comment}</TableCell>
                          </TableRow>
                        </Tooltip>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                  {t('shots_table_caption')}
                </Typography>

                <Grid size={{ xs: 12 }}>
                  {isMobile ? (
                    <Box>
                      <Box mb={4}>
                        <Card sx={{ height: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'black' }}>
                          <CardMedia
                            component="video"
                            src={videoSrc}
                            autoPlay
                            muted
                            loop
                            playsInline
                            sx={{ width: '100%', height: '100%', objectFit: 'fill' }}
                            onError={handleVideoError}
                          />
                        </Card>
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                          {t('video_caption')}
                        </Typography>
                      </Box>
                      <Box>
                        <Box
                          ref={graphCardRef}
                          sx={{
                            height: 800,
                            bgcolor: 'white',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            ...(highlightCard && {
                              boxShadow: `0 0 0 4px ${theme.palette.primary.main}, 0 0 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                              transition: 'box-shadow 0.3s ease-in-out',
                            }),
                          }}
                        >
                          {loading ? (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                              <CircularProgress />
                            </Box>
                          ) : shotData ? (
                            <ShotPlots data={shotData} />
                          ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                              <Typography variant="body1" color="text.secondary">
                                {t('select_shot_prompt')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                          {t('graph_caption')}{' '}
                          {currentShotId && (
                            <Typography component="span" variant="body2" fontWeight={900} color="primary.main">
                              {currentShotId}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box ref={containerRef} sx={{ display: 'flex', width: '100%', position: 'relative' }}>
                      <Box sx={{ flex: leftRatio, minWidth: 0, pr: 1 }}>
                        <Card sx={{ height: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'black' }}>
                          <CardMedia
                            component="video"
                            src={videoSrc}
                            autoPlay
                            muted
                            loop
                            playsInline
                            sx={{ width: '100%', height: '100%', objectFit: 'fill' }}
                            onError={handleVideoError}
                          />
                        </Card>
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                          {t('video_caption')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: '12px',
                          cursor: 'col-resize',
                          bgcolor: isDragging ? 'primary.main' : alpha(theme.palette.primary.main, 0.15),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.3),
                          },
                          transition: 'background-color 0.2s',
                          flexShrink: 0,
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseDown={handleMouseDown}
                      >
                        <DragHandleIcon
                          sx={{
                            fontSize: 20,
                            color: isDragging ? 'white' : 'primary.main',
                            transform: 'rotate(90deg)',
                            pointerEvents: 'none',
                            opacity: 0.7,
                            transition: 'opacity 0.2s',
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 100 - leftRatio, minWidth: 0, pl: 1 }}>
                        <Box
                          ref={graphCardRef}
                          sx={{
                            height: 800,
                            bgcolor: 'white',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            ...(highlightCard && {
                              boxShadow: `0 0 0 4px ${theme.palette.primary.main}, 0 0 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                              transition: 'box-shadow 0.3s ease-in-out',
                            }),
                          }}
                        >
                          {loading ? (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                              <CircularProgress />
                            </Box>
                          ) : shotData ? (
                            <ShotPlots data={shotData} />
                          ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                              <Typography variant="body1" color="text.secondary">
                                {t('select_shot_prompt')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                          {t('graph_caption')}{' '}
                          {currentShotId && (
                            <Typography component="span" variant="body2" fontWeight={900} color="primary.main">
                              {currentShotId}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </div>
        </Section>

        {/* 3. Диагностики */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.diagnostics} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography
              variant="h3"
              color="primary.dark"
              gutterBottom
              sx={{ mt: 8, fontSize: { xs: '2rem', md: '3rem' } }}
              textAlign="center"
            >
              {t('section_diagnostics')}
            </Typography>
              <Grid container spacing={2} sx={{ position: 'relative' }}>
                {diagnostics.map((item, idx) => (
                  <Grid
                    size={{ xs: 12, sm: 6, md: 4 }}
                    key={idx}
                    sx={{
                      position: 'relative',
                      height: 'auto',
                    }}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        height: '85px', // Высота карточек
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        borderRadius: 3,
                        '&:hover': {
                          boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-4px)',
                        },
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        // Абсолютное позиционирование, если карточка раскрыта или находится в процессе закрытия
                        ...((expandedDiagnostic === idx || closingDiagnostic === idx) && {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: 'auto',
                          zIndex: 1300,
                          boxShadow: `0 24px 80px ${alpha(theme.palette.primary.main, 0.3)}`,
                          borderColor: theme.palette.primary.main,
                          '& .MuiCardContent-root': {
                            maxHeight: '70vh',
                            overflowY: 'auto',
                          },
                          transform: 'none',
                          '&:hover': { transform: 'none' },
                        }),
                      }}
                      onClick={() => {
                        if (expandedDiagnostic === idx) {
                          setClosingDiagnostic(idx);
                          setExpandedDiagnostic(null);
                        } else {
                          setExpandedDiagnostic(idx);
                        }
                      }}
                    >
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: 2, color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {diagnosticIconsList[idx] || <ScienceIcon />}
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600}>{item.title}</Typography>
                          </Box>
                          <IconButton size="small">
                            {expandedDiagnostic === idx ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                        <Collapse 
                          in={expandedDiagnostic === idx}
                          onExited={() => {
                            setClosingDiagnostic(null);
                          }}
                        >
                          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                            <List dense disablePadding>
                              {item.description.map((desc, i) => (
                                <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: theme.palette.primary.main }} />
                                  </ListItemIcon>
                                  <ListItemText primary={parse(desc)} />
                                </ListItem>
                              ))}
                            </List>
                            {item.chips && item.chips.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                                {item.chips.map((chip, i) => <Chip key={i} label={chip} size="small" variant="outlined" />)}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Карточка "Любая диагностика" */}
                <Grid
                  size={{ xs: 12, sm: 6, md: 4 }}
                  sx={{
                    position: 'relative',
                    height: 'auto',
                  }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      height: '85px', // Высота карточек
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderRadius: 3,
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      opacity: 0.8,
                      '&:hover': { opacity: 1, borderColor: theme.palette.primary.main },
                      ...((expandedAnyDiagnostic || closingAny) && {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: 'auto',
                        zIndex: 1300,
                        boxShadow: `0 24px 80px ${alpha(theme.palette.primary.main, 0.3)}`,
                        borderColor: theme.palette.primary.main,
                        opacity: 1,
                        '& .MuiCardContent-root': { maxHeight: '70vh', overflowY: 'auto' },
                        transform: 'none',
                        '&:hover': { transform: 'none' },
                      }),
                    }}
                    onClick={() => {
                      if (expandedAnyDiagnostic) {
                        setClosingAny(true);
                        setExpandedAnyDiagnostic(false);
                      } else {
                        setExpandedAnyDiagnostic(true);
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}` }}>
                            <AddIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                          </Box>
                          <Typography variant="subtitle1" fontWeight={600}>{t('any_diagnostic_title')}</Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedAnyDiagnostic(!expandedAnyDiagnostic); }}>
                          {expandedAnyDiagnostic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      <Collapse 
                        in={expandedAnyDiagnostic}
                        onExited={() => {
                          setClosingAny(false);
                        }}
                      >
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}` }}>
                          <Typography variant="body2" color="text.secondary">{t('any_diagnostic_subtitle')}</Typography>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
          </div>
        </Section>

        {/* 4. Онлайн-платформа */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.online} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography
              variant="h3"
              color="primary.dark"
              gutterBottom
              sx={{ mt: 8, fontSize: { xs: '2rem', md: '3rem' } }}
              textAlign="center"
            >
              {t('section_online')}
            </Typography>
            <Typography variant="body1" paragraph textAlign="justify" sx={{ maxWidth: 'lg', mx: 'auto' }}>
              {t('online_platform_descr')}
            </Typography>
            <Grid container spacing={5}>
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateRows: `repeat(${onlineServices.length}, 1fr)`,
                    gap: 1,
                    alignItems: 'stretch',
                  }}
                >
                  {onlineServices.map((service, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScienceIcon color="primary" sx={{ mr: 1.5 }} />
                      <Box>
                        <Typography variant="body1">{service.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ImageCard
                  src={`${ASSETS_PATH}shots-table.png`}
                  alt={t('img_shots_table')}
                  height={160}
                  onClick={() => openImageDialog(`${ASSETS_PATH}shots-table.png`, t('img_shots_table'))}
                  t={t}
                  sx={{
                    mb: 2,
                    borderRadius: 4,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                    '& .image-media': {
                      objectPosition: 'center',
                    },
                  }}
                />
                <ImageCard
                  src={`${ASSETS_PATH}moodle.png`}
                  alt={t('img_moodle')}
                  height={160}
                  onClick={() => openImageDialog(`${ASSETS_PATH}moodle.png`, t('img_moodle'))}
                  t={t}
                  sx={{
                    mb: 2,
                    borderRadius: 4,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                    '& .image-media': {
                      objectPosition: 'center',
                    },
                  }}
                />
                <ImageCard
                  src={`${ASSETS_PATH}control-panel.png`}
                  alt={t('img_control_panel')}
                  height={160}
                  onClick={() => openImageDialog(`${ASSETS_PATH}control-panel.png`, t('img_control_panel'))}
                  t={t}
                  sx={{
                    mb: 2,
                    borderRadius: 4,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                    '& .image-media': {
                      objectPosition: 'center',
                    },
                  }}
                />
                <ImageCard
                  src={`${ASSETS_PATH}shots_vis.png`}
                  alt={t('shots_vis')}
                  height={160}
                  onClick={() => openImageDialog(`${ASSETS_PATH}shots_vis.png`, t('shots_vis'))}
                  t={t}
                  sx={{
                    borderRadius: 4,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                    '& .image-media': {
                      objectPosition: 'center',
                    },
                  }}
                />
              </Grid>
            </Grid>

            <ShotOrderCard
              onOrderClick={() => {
                const subject = encodeURIComponent(t('email_subject'));
                const body = encodeURIComponent(t('email_body'));
                window.location.href = `mailto:${t('email')}?subject=${subject}&body=${body}`;
              }}
            />

            <Paper sx={{ p: 4, mt: 4, bgcolor: alpha(theme.palette.primary.light, 0.05), borderRadius: 4 }}>
              <Typography variant="h4" color="primary.dark" gutterBottom textAlign="center">
                {t('workshop_title')}
              </Typography>
              <Typography variant="body1" paragraph textAlign="justify" sx={{ maxWidth: 'lg', mx: 'auto' }}>
                {t('workshop_descr')}
              </Typography>
              <Grid container spacing={4} alignItems="center" sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <List
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: { xs: 'auto', md: 450 },
                    }}
                  >
                    {workshopItems.map((topic, idx) => (
                      <ListItem key={idx} sx={{ py: { xs: 1, md: 2 } }}>
                        <ListItemIcon>
                          <SchoolIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={topic} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box sx={{ position: 'relative' }}>
                    <ImageCard
                      src={`${ASSETS_PATH}poster.jpg`}
                      alt={t('workshop_poster_alt')}
                      height={450}
                      onClick={() => openImageDialog(`${ASSETS_PATH}poster.jpg`, t('workshop_poster_alt'))}
                      t={t}
                      sx={{
                        borderRadius: 6,
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 6,
                        },
                        '& .image-media': {
                          objectPosition: 'top center',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 80,
                        background: `linear-gradient(to top, ${alpha(theme.palette.background.default, 0.7)} 0%, transparent 100%)`,
                        pointerEvents: 'none',
                        borderRadius: '0 0 8px 8px',
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.primary"
                      align="center"
                      sx={{
                        mt: 1.5,
                        fontStyle: 'italic',
                        fontWeight: 500,
                      }}
                    >
                      {t('workshop_poster_caption')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </div>
        </Section>

        {/* 5. Токамак под ключ */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.turnkey} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography
              variant="h3"
              color="primary.dark"
              gutterBottom
              sx={{ mt: 8, fontSize: { xs: '2rem', md: '3rem' } }}
              textAlign="center"
            >
              {t('section_turnkey')}
            </Typography>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TurnkeySlideshow t={t} ASSETS_PATH={ASSETS_PATH} openImageDialog={openImageDialog} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body1" paragraph textAlign="justify">
                  {t('direction_turnkey_desc')}
                </Typography>
                <List>
                  {experienceItems.map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>

            <Paper sx={{ p: 4, mt: 4, bgcolor: alpha(theme.palette.primary.light, 0.05), borderRadius: 4 }}>
              <Typography variant="h4" color="primary.dark" gutterBottom textAlign="center">
                {t('control_title')}
              </Typography>
              <Typography variant="body1" paragraph textAlign="justify">
                {t('control_desc')}
              </Typography>
              <List>
                {controlFeatures.map((feature, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <SettingsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </div>
        </Section>

        {/* 6. Опциональное оборудование */}
        <Section index={sectionCounter++}>
          <Box sx={{ my: 8 }}>
            <Typography variant="h4" color="primary.dark" gutterBottom textAlign="center">
              {t('optional_title')}
            </Typography>
            <Grid container spacing={3}>
              {optionalItems.map((item, idx) => (
                <Grid size={{ xs: 12, md: 4 }} key={idx}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      {item.name}
                    </Typography>
                    <Typography variant="body2">{item.desc}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Section>

        {/* 7. Образовательная программа */}
        <Section index={sectionCounter++}>
          <Paper sx={{ p: 4, my: 8, bgcolor: alpha(theme.palette.primary.light, 0.05), borderRadius: 4 }}>
            <Typography variant="h4" color="primary.dark" gutterBottom textAlign="center">
              {t('education_title')}
            </Typography>
            <Typography variant="body1" paragraph textAlign="justify">
              {t('education_desc')}
            </Typography>
            <List>
              {educationFeatures.map((feat, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <SchoolIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={feat} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Section>

        {/* 8. Призыв к действию */}
        <Section index={sectionCounter++}>
          <Box sx={{ textAlign: 'center', my: 8 }}>
            <Typography variant="h3" color="primary.dark" gutterBottom>
              {t('cta_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              {t('cta_text')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setAccessModalOpen(true)}
              sx={{ borderRadius: 4, px: 5, py: 1.5 }}
            >
              {t('cta_button')}
            </Button>
          </Box>
        </Section>

        {/* 9. Публикации */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.publications} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Typography variant="h4" color="primary.dark" gutterBottom textAlign="center">
              {t('section_publications')}
            </Typography>
            <Paper sx={{ p: 3, maxHeight: 400, overflow: 'auto' }}>
              <List dense>
                {publicationsData.map((pub, idx) => (
                  <ListItem key={idx} sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <MenuBookIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          {pub.text}
                          <Link href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener">
                            doi: {pub.doi}
                          </Link>
                          .
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </div>
        </Section>

        {/* 10. Контакты */}
        <Section index={sectionCounter++}>
          <div ref={sectionRefs.contacts} style={{ scrollMarginTop: isMobile ? 200 : 120 }}>
            <Paper sx={{ p: 4, my: 8, bgcolor: alpha(theme.palette.primary.light, 0.05), borderRadius: 4 }}>
              <Typography variant="h3" color="primary.dark" gutterBottom textAlign="center">
                {t('contacts_title')}
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('contacts_email')}
                        secondary={<Link href="mailto:tokamak@mephi.ru">tokamak@mephi.ru</Link>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={t('contacts_phone')} secondary={t('ext_num')} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <TelegramIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('contacts_telegram')}
                        secondary={
                          <Link href="https://t.me/plasmaMEPhI" target="_blank" rel="noopener">
                            t.me/plasmaMEPhI
                          </Link>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DescriptionIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('contacts_license')}
                        secondary={
                          <Link href="https://tokamak.mephi.ru/license/" target="_blank" rel="noopener">
                            tokamak.mephi.ru/license
                          </Link>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <SchoolIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('contacts_mephi')}
                        secondary={
                          <Link href="https://mephi.ru/" target="_blank" rel="noopener">
                            mephi.ru
                          </Link>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <SchoolIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('contacts_admission')}
                        secondary={
                          <Link href="https://admission.mephi.ru/" target="_blank" rel="noopener">
                            admission.mephi.ru
                          </Link>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <ImageCard
                      src={`${ASSETS_PATH}qr-code.png`}
                      alt={t('qr_code_alt')}
                      height={150}
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        openImageDialog(`${ASSETS_PATH}qr-code.png`, t('qr_code_alt'));
                      }}
                      t={t}
                      sx={{
                        maxWidth: '100%',
                        width: 'auto',
                        border: 'none',
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </div>
        </Section>

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" align="center" color="text.secondary">
          {t('footer_text').replace('{year}', new Date().getFullYear().toString())}
        </Typography>
      </Container>

{/* Плавающие кнопки действий (появляются при скролле вниз) */}
<Fade in={scrolledPastHero} timeout={300}>
  <Box
    sx={{
      position: 'fixed',
      bottom: 24,
      right: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      zIndex: 1000,
    }}
  >

    
    
    <Tooltip title={t('enter_platform')} arrow placement="left">
      <Fab
        color="primary"
        size="small"
        component="a"
        href="https://tokamak.mephi.ru/orders"
        target="_blank"
        rel="noopener"
      >
        <LoginIcon />
      </Fab>
    </Tooltip>
    {/* 
    
    <Tooltip title={t('toggle_language')} arrow placement="left">
      <Fab color="primary" size="small" onClick={toggleLanguage}>
        <LanguageIcon />
      </Fab>
    </Tooltip>

  
    <Tooltip title={t('hero_cta')} arrow placement="left">
      <Fab color="primary" size="small" onClick={() => setAccessModalOpen(true)}>
        <EmailIcon />
      </Fab>
    </Tooltip> */}

    {/* Наверх */}
    <Tooltip title={t('to_top')} arrow placement="left">
      <Fab
        color="primary"
        size="small"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ArrowUpwardIcon />
      </Fab>
    </Tooltip>
  </Box>
</Fade>

      <ImageDialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} src={dialogImageSrc} alt={dialogImageAlt} />

      <Dialog open={accessModalOpen} onClose={() => setAccessModalOpen(false)} disableScrollLock>
        <DialogTitle>
          {t('modal_access_title')}
          <IconButton onClick={() => setAccessModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>{t('modal_access_text')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <EmailIcon color="primary" />
            <Link href={`mailto:${t('email')}?subject=${t('email_subject2')}`} variant="h6">
              {t('email')}
            </Link>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('modal_access_license_before')}{' '}
            <Link href="https://tokamak.mephi.ru/license/" target="_blank" rel="noopener">
              {t('modal_access_license_link')}
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessModalOpen(false)}>{t('modal_close')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={orderModalOpen} onClose={() => setOrderModalOpen(false)} disableScrollLock>
        <DialogTitle>
          {t('order_modal_title')}
          <IconButton onClick={() => setOrderModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>{t('order_modal_text')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <EmailIcon color="primary" />
            <Link href={`mailto:${t('email')}`} variant="h6">
              {t('email')}
            </Link>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('modal_access_license_before')}{' '}
            <Link href="https://tokamak.mephi.ru/license/" target="_blank" rel="noopener">
              {t('modal_access_license_link')}
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderModalOpen(false)}>{t('modal_close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitingCard;